/**
 * Lil Teasers - Custom Theme JavaScript
 * Handles: Mega Menu, Hero Carousel, Product Card Hover Preload, Smooth Scroll
 */

// ============================================================================
// MEGA MENU - Enhanced hover/focus behavior
// ============================================================================

class MegaMenu {
  constructor() {
    this.menus = document.querySelectorAll('.mega-menu');
    this.init();
  }

  init() {
    this.menus.forEach(menu => {
      const summary = menu.querySelector('summary');
      const content = menu.querySelector('.mega-menu__content');

      if (!summary || !content) return;

      // Hover intent for desktop
      let hoverTimeout;
      let leaveTimeout;

      const handleMouseEnter = () => {
        clearTimeout(leaveTimeout);
        hoverTimeout = setTimeout(() => {
          menu.setAttribute('open', '');
          summary.setAttribute('aria-expanded', 'true');
        }, 150);
      };

      const handleMouseLeave = () => {
        clearTimeout(hoverTimeout);
        leaveTimeout = setTimeout(() => {
          menu.removeAttribute('open');
          summary.setAttribute('aria-expanded', 'false');
        }, 150);
      };

      menu.addEventListener('mouseenter', handleMouseEnter);
      menu.addEventListener('mouseleave', handleMouseLeave);

      // Keyboard support
      summary.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          menu.toggleAttribute('open');
          summary.setAttribute('aria-expanded', menu.hasAttribute('open'));
        }
        if (e.key === 'Escape') {
          menu.removeAttribute('open');
          summary.setAttribute('aria-expanded', 'false');
          summary.focus();
        }
      });

      // Focus management
      content.addEventListener('focusin', () => {
        clearTimeout(leaveTimeout);
      });

      content.addEventListener('focusout', (e) => {
        if (!menu.contains(e.relatedTarget)) {
          leaveTimeout = setTimeout(() => {
            menu.removeAttribute('open');
            summary.setAttribute('aria-expanded', 'false');
          }, 100);
        }
      });

      // Close on click outside
      document.addEventListener('click', (e) => {
        if (!menu.contains(e.target)) {
          menu.removeAttribute('open');
          summary.setAttribute('aria-expanded', 'false');
        }
      });
    });

    // Close on escape key globally
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.menus.forEach(menu => {
          if (menu.hasAttribute('open')) {
            menu.removeAttribute('open');
            const summary = menu.querySelector('summary');
            if (summary) summary.setAttribute('aria-expanded', 'false');
          }
        });
      }
    });
  }
}

// ============================================================================
// HERO CAROUSEL
// ============================================================================

class HeroCarousel {
  constructor(element) {
    this.carousel = element;
    this.track = this.carousel.querySelector('.hero-carousel__track');
    this.slides = this.carousel.querySelectorAll('.hero-carousel__slide');
    this.prevBtn = this.carousel.querySelector('[data-action="prev"]');
    this.nextBtn = this.carousel.querySelector('[data-action="next"]');
    this.dots = this.carousel.querySelectorAll('.hero-carousel__dot');
    this.currentIndex = 0;
    this.autoplay = this.carousel.dataset.autoplay === 'true';
    this.speed = parseInt(this.carousel.dataset.speed) || 5000;
    this.autoplayTimer = null;
    this.isAnimating = false;

    this.init();
  }

  init() {
    if (this.slides.length <= 1) return;

    // Button events
    this.prevBtn?.addEventListener('click', () => this.prev());
    this.nextBtn?.addEventListener('click', () => this.next());

    // Dot events
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goTo(index));
    });

    // Keyboard
    this.carousel.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.prev();
      if (e.key === 'ArrowRight') this.next();
    });

    // Touch swipe
    this.initTouch();

    // Autoplay
    if (this.autoplay) {
      this.startAutoplay();
      this.carousel.addEventListener('mouseenter', () => this.stopAutoplay());
      this.carousel.addEventListener('mouseleave', () => this.startAutoplay());
    }

    // Pause on visibility change
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stopAutoplay();
      else if (this.autoplay) this.startAutoplay();
    });
  }

  initTouch() {
    let startX = 0;
    let currentX = 0;
    let isDragging = false;

    this.track.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      isDragging = true;
      this.stopAutoplay();
    }, { passive: true });

    this.track.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      currentX = e.touches[0].clientX;
    }, { passive: true });

    this.track.addEventListener('touchend', () => {
      if (!isDragging) return;
      isDragging = false;

      const diff = startX - currentX;
      const threshold = 50;

      if (Math.abs(diff) > threshold) {
        if (diff > 0) this.next();
        else this.prev();
      }

      if (this.autoplay) this.startAutoplay();
    }, { passive: true });
  }

  goTo(index) {
    if (this.isAnimating || index === this.currentIndex) return;
    if (index < 0) index = this.slides.length - 1;
    if (index >= this.slides.length) index = 0;

    this.isAnimating = true;
    this.track.style.transform = `translateX(-${index * 100}%)`;

    // Update active slide
    this.slides[this.currentIndex].removeAttribute('aria-current');
    this.slides[index].setAttribute('aria-current', 'true');

    // Update dots
    this.dots[this.currentIndex]?.classList.remove('hero-carousel__dot--active');
    this.dots[index]?.classList.add('hero-carousel__dot--active');

    this.currentIndex = index;

    setTimeout(() => {
      this.isAnimating = false;
    }, 600);
  }

  next() {
    this.goTo(this.currentIndex + 1);
  }

  prev() {
    this.goTo(this.currentIndex - 1);
  }

  startAutoplay() {
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => this.next(), this.speed);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }
}

// ============================================================================
// PRODUCT CARD - Hover Image Preload
// ============================================================================

class ProductCardHover {
  constructor() {
    this.cards = document.querySelectorAll('.product-card--lil-teasers');
    this.init();
  }

  init() {
    if (!('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const card = entry.target;
          const secondaryImg = card.querySelector('.product-card__image--secondary');
          if (secondaryImg && secondaryImg.dataset.src) {
            // Preload the secondary image
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'image';
            link.href = secondaryImg.dataset.src;
            document.head.appendChild(link);
          }
          this.observer.unobserve(card);
        }
      });
    }, {
      rootMargin: '200px',
      threshold: 0.1
    });

    this.cards.forEach(card => this.observer.observe(card));
  }
}

// ============================================================================
// SMOOTH SCROLL for anchor links
// ============================================================================

function initSmoothScroll() {
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function(e) {
      const targetId = this.getAttribute('href');
      if (targetId === '#') return;

      const target = document.querySelector(targetId);
      if (target) {
        e.preventDefault();
        const headerOffset = 100;
        const elementPosition = target.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });

        target.focus({ preventScroll: true });
      }
    });
  });
}

// ============================================================================
// INITIALIZATION
// ============================================================================

document.addEventListener('DOMContentLoaded', () => {
  // Initialize Mega Menu
  new MegaMenu();

  // Initialize Hero Carousels
  document.querySelectorAll('.hero-carousel[data-autoplay]').forEach(el => {
    new HeroCarousel(el);
  });

  // Initialize Product Card Hover Preload
  new ProductCardHover();

  // Initialize Smooth Scroll
  initSmoothScroll();

  // Add loaded class for CSS animations
  document.body.classList.add('js-loaded');
});

// ============================================================================
// RESIZE HANDLER - Debounced
// ============================================================================

let resizeTimer;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    document.dispatchEvent(new CustomEvent('theme:resize'));
  }, 250);
});